import { Col, Row, Label } from "reactstrap";
import React, { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import AppModal from "../../Common/AppModal";

import {
  createOrganisationDepartmentToStaff,
  createOrganisationDepartmentToStaffSuccess,
  getOrganisationDepartmentList,
  getOrganisationDepartmentToStaffList,
  getStaffList,
  searchOrganisationDepartmentToStaff,
  updateOrganisationDepartmentToStaff,
  updateOrganisationDepartmentToStaffSuccess,
} from "../../../store/actions";
import Select from "../../Common/AppSelect";
import filterActiveStaff from "../../Common/staffActive";
import { AppDatePicker } from "../../Common/AppDateTimePicker";
import moment from "moment";
import { timeFormatDateAndTime } from "../../Common/HandleDateTime";

export default function CreateEditDepartmentToStaff({
  modal,
  setModal,
  toggle,
  isEdit,
  updateData,
  staff_Id,
  // Task 24459 - opened from a department's side panel, so the department is
  // already decided and the picker for it is locked.
  preselectedDepartmentId,
}) {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getStaffList());
    dispatch(getOrganisationDepartmentList());
    // Needed to exclude people already in the chosen department (Task 24457).
    dispatch(getOrganisationDepartmentToStaffList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultData = {
    OrganisationDepartment_Id: preselectedDepartmentId ?? "",
    Staff_Id: staff_Id ?? "",
    FlagManager: "0",
    StartDateTime: new Date(),
  };

  const [data, setData] = useState(defaultData);
  // Task 24457 - assigning people one modal at a time was the complaint. When
  // creating, the staff picker is multi-select and one row is written per
  // person; edit still works on the single existing row.
  const [staffIds, setStaffIds] = useState([]);
  useEffect(() => {
    if (modal) {
      setStaffIds(staff_Id ? [staff_Id] : []);
      if (!isEdit) {
        setData({
          ...defaultData,
          OrganisationDepartment_Id: preselectedDepartmentId ?? "",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal]);

  useEffect(() => {
    if (!isEdit) return;
    if (isEmpty(updateData)) return;
    setData({
      Id: updateData?.Id,
      OrganisationDepartment_Id: updateData?.OrganisationDepartment_Id,
      Staff_Id: updateData?.Staff_Id,
    });
  }, [updateData, isEdit]);

  const handleCreateUpdate = (type) => {
    // check type
    if (type === "create") {
      if (!data?.OrganisationDepartment_Id) {
        toast.error("Choose a department");
        return;
      }
      if (!staffIds.length) {
        toast.error("Choose at least one staff member");
        return;
      }
      // The endpoint already loops the array and validates every row before it
      // writes any of them, so a multi-assign is the same call with more rows.
      dispatch(
        createOrganisationDepartmentToStaff(
          staffIds.map((id) => ({ ...data, Staff_Id: id }))
        )
      );
    }
    if (type === "update") {
      dispatch(updateOrganisationDepartmentToStaff([data]));
    }
  };

  const {
    createOrganisationDepartmentSuccessAPI,
    updateOrganisationDepartmentSuccessAPI,
    loadingCreateOrganisationDepartmentToStaff,
    loadingUpdateOrganisationDepartmentToStaff,
  } = useSelector((state) => ({
    createOrganisationDepartmentSuccessAPI:
      state.Staff.createOrganisationDepartmentToStaffSuccess,
    updateOrganisationDepartmentSuccessAPI:
      state.Staff.updateOrganisationDepartmentToStaffSuccess,
    loadingCreateOrganisationDepartmentToStaff:
      state.Staff.loadingCreateOrganisationDepartmentToStaff,
    loadingUpdateOrganisationDepartmentToStaff:
      state.Staff.loadingUpdateOrganisationDepartmentToStaff,
  }));
  const { organisationDepartmentListAPI, loadingOrganisationDepartmentList } =
    useSelector((state) => ({
      organisationDepartmentListAPI:
        state.Staff.organisationDepartmentList?.organisationdepartment_list,
      loadingOrganisationDepartmentList:
        state.Staff.loadingOrganisationDepartmentList,
    }));
  const { staffData } = useSelector((state) => ({
    staffData: state.Staff.staffList?.staff_list,
  }));
  const { departmentToStaffListAPI } = useSelector((state) => ({
    departmentToStaffListAPI:
      state.Staff.organisationDepartmentToStaffList
        ?.organisationdepartmenttostaff_list,
  }));

  // Ids already in the chosen department. Offering them would only earn a 409
  // ("already exists") that rejects the WHOLE batch, so they are filtered out
  // of the options instead.
  const alreadyAssignedIds = new Set(
    (departmentToStaffListAPI || [])
      .filter(
        (row) =>
          String(row?.OrganisationDepartment_Id) ===
          String(data?.OrganisationDepartment_Id)
      )
      .map((row) => String(row?.Staff_Id))
  );

  const staffOptions = (
    !isEmpty(staffData) ? filterActiveStaff(staffData, staffIds) : []
  )
    .filter(
      (item) =>
        isEdit ||
        staffIds.includes(item?.Id) ||
        !alreadyAssignedIds.has(String(item?.Id))
    )
    .map((item) => ({
      label:
        item?.FullName ||
        [item?.FirstName, item?.LastName].filter(Boolean).join(" "),
      value: item?.Id,
      id: item?.Id,
      jobTitle: item?.JobTitle || "",
    }));

  // Change #100317 - job title under the name in the open menu, so two people
  // with similar names (or "which one is the tech?") can be told apart. The
  // chosen chips stay name-only to keep the control compact.
  const formatStaffOption = (option, { context }) =>
    context === "menu" && option?.jobTitle ? (
      <div>
        <div>{option.label}</div>
        <div className="text-muted small">{option.jobTitle}</div>
      </div>
    ) : (
      option?.label
    );
  // Typing a job title ("technician") finds the people with it too.
  const filterStaffOption = (option, input) => {
    const needle = (input || "").trim().toLowerCase();
    if (!needle) return true;
    return `${option?.label || ""} ${option?.data?.jobTitle || ""}`
      .toLowerCase()
      .includes(needle);
  };
  const helperCallFreshData = () => {
    dispatch(getOrganisationDepartmentToStaffList());
    if(staff_Id)
    {
      dispatch(
        searchOrganisationDepartmentToStaff([
          {
            staff_data: {
              Id: staff_Id,
            },
          },
        ])
      );
    }  };

  useEffect(() => {
    if (isEmpty(createOrganisationDepartmentSuccessAPI)) return;
    if (createOrganisationDepartmentSuccessAPI?.status === 200) {
      toast.success(createOrganisationDepartmentSuccessAPI?.message);
      helperCallFreshData();
      setModal(false);
    } else {
      toast.error(createOrganisationDepartmentSuccessAPI?.message);
    }
    dispatch(createOrganisationDepartmentToStaffSuccess([]));
  }, [createOrganisationDepartmentSuccessAPI]);

  useEffect(() => {
    if (isEmpty(updateOrganisationDepartmentSuccessAPI)) return;
    if (updateOrganisationDepartmentSuccessAPI?.status === 200) {
      toast.success(updateOrganisationDepartmentSuccessAPI?.message);
      helperCallFreshData();
      setModal(false);
    } else {
      toast.error(updateOrganisationDepartmentSuccessAPI?.message);
    }
    dispatch(updateOrganisationDepartmentToStaffSuccess([]));
  }, [updateOrganisationDepartmentSuccessAPI]);

  return (
    <>
      <AppModal
        open={modal}
        onClose={toggle}
        title={
          isEdit ? "Edit Department To Staff" : "Create New Department To Staff"
        }
        icon={<AccountTreeOutlinedIcon />}
        headerAccent="indigo"
        size="sm"
        loading={
          loadingCreateOrganisationDepartmentToStaff ||
          loadingUpdateOrganisationDepartmentToStaff
        }
        loadingText="Saving…"
        submitLabel={isEdit ? "Update" : "Create"}
        submitColor="success"
        onSubmit={() => handleCreateUpdate(isEdit ? "update" : "create")}
      >
          <Row className="g-3">
            <Col lg={12}>
              <div>
                <div className={"pt-0"}>
                  <div>
                    <Label className="form-label mt-2" for={"UrlLink"}>
                      Select Department
                    </Label>
                    <div>
                      If Department is a sub department, select the parent
                      department.
                    </div>
                    <Select
                      isClearable={!preselectedDepartmentId}
                      isDisabled={!!preselectedDepartmentId}
                      placeholder={"Select Parent Department"}
                      className="react-select-container"
                      value={{
                        value: data?.OrganisationDepartment_Id,
                        label: organisationDepartmentListAPI?.find(
                          (item) => item.Id === data?.OrganisationDepartment_Id
                        )?.Name,
                        id: data?.OrganisationDepartment_Id,
                      }}
                      options={
                        organisationDepartmentListAPI?.map((item) => ({
                          value: item.Id,
                          label: item.Name,
                          id: item.Id,
                        })) || []
                      }
                      onChange={(e) => {
                        setData({
                          ...data,
                          OrganisationDepartment_Id: e ? e.id : "",
                        });
                      }}
                    ></Select>
                  </div>
                  <div>
                    <Label className="form-label mt-2" for={"UrlLink"}>
                      {isEdit ? "Select Staff member" : "Select staff members"}
                    </Label>
                    {!isEdit && (
                      <div className={"text-muted small mb-1"}>
                        Pick as many as you like — one assignment is created for
                        each. Anyone already in this department is left out of
                        the list.
                      </div>
                    )}
                    <div className={"d-flex justify-content-between"}>
                      <Select
                        className="mb-0 job-select2 flex-grow-1"
                        isMulti={!isEdit}
                        isDisabled={!isEdit && !data?.OrganisationDepartment_Id}
                        placeholder={
                          !isEdit && !data?.OrganisationDepartment_Id
                            ? "Choose a department first…"
                            : "Select..."
                        }
                        value={
                          isEdit
                            ? data.Staff_Id
                              ? staffOptions.find(
                                  (o) => o.value === data.Staff_Id
                                ) || null
                              : null
                            : staffOptions.filter((o) =>
                                staffIds.includes(o.value)
                              )
                        }
                        onChange={(e) => {
                          if (isEdit) {
                            setData({ ...data, Staff_Id: e ? e.value : "" });
                            setStaffIds(e ? [e.value] : []);
                          } else {
                            setStaffIds((e || []).map((o) => o.value));
                          }
                        }}
                        options={staffOptions}
                        formatOptionLabel={formatStaffOption}
                        filterOption={filterStaffOption}
                        id="country-select"
                      ></Select>

                      <div className={"d-flex align-items-center ms-2"}>
                        <input
                          style={{ width: "38px", height: "38px", margin: 0 }}
                          className={"form-check-input"}
                          type="checkbox"
                          value={data.FlagManager === "1"}
                          onChange={(e) => {
                            setData({
                              ...data,
                              FlagManager: e.target.checked ? "1" : "0",
                            });
                          }}
                        />{" "}
                        <span className={"ms-1"}>Manager</span>
                      </div>
                    </div>
                  </div>
                  <div className={"mt-2"}>
                    <Label>Select Start Date</Label>
                    <AppDatePicker
                      value={
                        data.StartDateTime
                          ? moment(data.StartDateTime, "YYYY-MM-DD HH:mm:ss")
                          : null
                      }
                      onChange={(val) => {
                        const jsDate =
                          val && val.isValid() ? val.toDate() : null;
                        setData({
                          ...data,
                          StartDateTime: jsDate
                            ? timeFormatDateAndTime(jsDate)
                            : "",
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
      </AppModal>
    </>
  );
}
